import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, MODULES, i18n } from "@/lib/api";

function renderMarkdownWithCitationChips(markdown, onCiteClick) {
  // Replace [n] tokens with placeholder spans that we'll inject via components.code
  // Simpler approach: post-process via dangerouslySet replacement inside text nodes.
  return markdown.replace(/\[(\d+)\]/g, (_, n) => `\u2329${n}\u232A`);
}

export default function ResultsPane({ document, language, onFeedback }) {
  const t = i18n[language];
  const [speaking, setSpeaking] = useState(false);
  const [citationOpen, setCitationOpen] = useState(null);

  const md = document?.processed_analysis || "";
  const citations = document?.citations_json?.index || [];

  const decoratedMd = useMemo(() => renderMarkdownWithCitationChips(md), [md]);

  useEffect(() => {
    return () => {
      try {
        window.speechSynthesis?.cancel();
      } catch (e) {
        /* noop */
      }
    };
  }, [document?.document_id]);

  const startListen = () => {
    if (!("speechSynthesis" in window)) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(md.replace(/[#*>`]/g, ""));
      utter.lang = language === "es" ? "es-ES" : "en-US";
      utter.rate = 0.95;
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utter);
      setSpeaking(true);
      // MediaSession - lock screen persistence
      if ("mediaSession" in navigator) {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: document?.custom_document_name || document?.document_title || "POCKET TA AI document",
          artist: "POCKET TA AI",
          album: "GO OUTDOORS",
        });
        navigator.mediaSession.setActionHandler?.("play", () => startListen());
        navigator.mediaSession.setActionHandler?.("pause", () => stopListen());
      }
    } catch (e) {
      setSpeaking(false);
    }
  };

  const stopListen = () => {
    try {
      window.speechSynthesis?.cancel();
    } catch (e) {
      /* noop */
    }
    setSpeaking(false);
  };

  if (!document) return null;

  // Render the markdown with citation chip replacements
  const components = {
    p: ({ children }) => {
      const processed = React.Children.toArray(children).map((child, idx) => {
        if (typeof child === "string") {
          const parts = child.split(/(\u2329\d+\u232A)/g);
          return parts.map((p, i) => {
            const m = p.match(/^\u2329(\d+)\u232A$/);
            if (m) {
              const n = parseInt(m[1], 10);
              return (
                <button
                  key={`c-${idx}-${i}`}
                  className="cite-chip"
                  data-testid={`citation-chip-${n}`}
                  onClick={() => setCitationOpen(n)}
                  title={`Reference ${n}`}
                >
                  [{n}]
                </button>
              );
            }
            return <React.Fragment key={`t-${idx}-${i}`}>{p}</React.Fragment>;
          });
        }
        return child;
      });
      return <p>{processed}</p>;
    },
    li: ({ children }) => {
      const processed = React.Children.toArray(children).map((child, idx) => {
        if (typeof child === "string") {
          const parts = child.split(/(\u2329\d+\u232A)/g);
          return parts.map((p, i) => {
            const m = p.match(/^\u2329(\d+)\u232A$/);
            if (m) {
              const n = parseInt(m[1], 10);
              return (
                <button
                  key={`cl-${idx}-${i}`}
                  className="cite-chip"
                  data-testid={`citation-chip-${n}`}
                  onClick={() => setCitationOpen(n)}
                  title={`Reference ${n}`}
                >
                  [{n}]
                </button>
              );
            }
            return <React.Fragment key={`tl-${idx}-${i}`}>{p}</React.Fragment>;
          });
        }
        return child;
      });
      return <li>{processed}</li>;
    },
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  };

  return (
    <div data-testid="results-pane" style={{ marginTop: "1.5rem" }}>
      <div style={{ display: "flex", gap: "1.5rem", alignItems: "center", marginBottom: "0.85rem" }}>
        <button
          data-testid="audio-start"
          className="btn-ghost"
          onClick={startListen}
          disabled={speaking}
          aria-label={t.listen}
        >
          {t.listen}
        </button>
        <button
          data-testid="audio-stop"
          className="btn-ghost"
          onClick={stopListen}
          disabled={!speaking}
          aria-label={t.stopListen}
        >
          {t.stopListen}
        </button>
        <div className="kbd" style={{ marginLeft: "auto" }}>
          {document.module_number ? MODULES.find((m) => m.id === document.module_number)?.[language] : ""}
        </div>
      </div>

      <div className="line" />

      <article className="markdown-body" data-testid="markdown-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {decoratedMd}
        </ReactMarkdown>
      </article>

      {citations.length > 0 && (
        <div data-testid="grounding-sources" style={{ marginTop: "2rem" }}>
          <div className="h-eyebrow">{t.citations}</div>
          <ol style={{ paddingLeft: "1.4rem", marginTop: "0.5rem" }}>
            {citations.map((c, i) => (
              <li key={i} className="txt-mute" style={{ fontSize: "0.92rem", marginBottom: "0.3rem" }}>
                <a href={c.uri} target="_blank" rel="noopener noreferrer">
                  {c.title || c.uri}
                </a>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="line-strong" />

      <div style={{ display: "flex", gap: "1.25rem", alignItems: "center" }}>
        <button data-testid="feedback-up" className="btn-link" onClick={() => onFeedback("up")}>
          {t.feedbackUp}
        </button>
        <button data-testid="feedback-down" className="btn-link" onClick={() => onFeedback("down")}>
          {t.feedbackDown}
        </button>
        <button data-testid="feedback-report" className="btn-link" onClick={() => onFeedback("report")}>
          {t.reportIssue}
        </button>
      </div>

      {citationOpen !== null && (
        <div
          data-testid="citation-drawer"
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: "min(420px, 100vw)",
            background: "var(--bg)",
            borderLeft: "1px solid var(--ink-faint)",
            padding: "2rem 1.5rem",
            zIndex: 50,
            overflowY: "auto",
          }}
        >
          <div className="h-eyebrow">Reference [{citationOpen}]</div>
          <div className="line" />
          {citations[citationOpen - 1] ? (
            <>
              <div style={{ marginBottom: "0.5rem", fontWeight: 600 }}>
                {citations[citationOpen - 1].title || citations[citationOpen - 1].uri}
              </div>
              <div className="mono" style={{ fontSize: "0.82rem", marginBottom: "1.25rem", wordBreak: "break-all" }}>
                {citations[citationOpen - 1].uri}
              </div>
              <a href={citations[citationOpen - 1].uri} target="_blank" rel="noopener noreferrer">
                Open Source
              </a>
            </>
          ) : (
            <p className="txt-mute">No structured reference is attached to this marker.</p>
          )}
          <div className="line" />
          <button
            data-testid="citation-drawer-close"
            className="btn-ghost"
            onClick={() => setCitationOpen(null)}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
